<template>
  <div class="tw-w-full">
    <UIDropDown v-model="visible" :title="props.placeholder">
      <div
        class="tw-input tw-input-md md:tw-input-xl lg:tw-input-md tw-border tw-px-3 md:tw-px-5 tw-input-bordered tw-flex tw-items-center tw-rounded-none"
        :class="[
          {
            'tw-border-error tw-border': !!errorMessages?.length,
            'hover:tw-border hover:tw-border-primary': !disabled,
            'tw-bg-stone-100 tw-text-neutral-600 tw-cursor-not-allowed': disabled,
          },
        ]" @click="open">
        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-1 tw-justify-start tw-cursor-pointer tw-py-1"
          v-if="selected?.length">
          <div v-for="(code, i) in selected" class="tw-flex tw-gap-2"
            :class="{ 'tw-badge tw-badge-info tw-h-[30px] tw-badge-lg tw-rounded': multi }">
            <span v-if="code" :title="getItem(code)?.name_en" :class="`fi fi-${code.toLocaleLowerCase()}`"></span>
            <span :class="{
              'tw-truncate tw-max-w-[100px] tw-text-sm': multi,
            }">{{ getItem(code)?.name_en }}</span>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
              class="tw-inline-block tw-h-4 tw-w-4 tw-stroke-current" v-if="multi" @click.stop="del(code)">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </div>
        </div>
        <div class="tw-grow tw-text-[#9ca3af] tw-truncate">
          <span v-if="!selected.length">{{ placeholder }}</span>
        </div>
        <span v-if="isPending" class="tw-loading tw-loading-spinner tw-loading-xs" />
        <SvgoCommonSelector v-else class="tw-w-[10px] tw-h-[7px] tw-cursor-pointer tw-fill-primary " />
      </div>

      <template #content>
        <label
          class="tw-input tw-input-sm tw-w-full tw-flex tw-items-center tw-gap-2 tw-border-0 focus-within:tw-border-0 focus-within:tw-outline-0 tw-rounded-none tw-px-1 md:tw-px-4">
          <input type="text" class="tw-grow" :placeholder="$t('kyc.search')" v-model.trim="searchValue" />
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor"
            class="tw-h-5 tw-w-5 tw-opacity-70">
            <path fill-rule="evenodd"
              d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
              clip-rule="evenodd" />
          </svg>
        </label>
        <div
          class="tw-min-h-[360px] md:tw-min-h-0 tw-max-h-[360px] tw-overflow-y-auto tw-pt-1 tw-border-t tw-border-t-[#ccc]/40">
          <div class="tw-flex tw-flex-col tw-flex-1 tw-gap-0.5" v-if="regions.length">
            <div v-for="item in regions" :key="item.name_en"
              class="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-2 tw-text-base tw-gap-1"
              :class="{
                'tw-text-neutral/40 tw-cursor-not-allowed': isRisky(item),
                'tw-cursor-pointer': !isRisky(item),
                'tw-bg-neutral/10 tw-rounded': isChecked(item.iso2_code),
              }" @click.stop="onSelect(item)">
              <span v-if="item.iso2_code" :class="`fi fi-${item.iso2_code.toLocaleLowerCase()}`"></span>
              <div class="tw-flex tw-gap-2 tw-items-center tw-text-right">
                <span v-html="`${isRisky(item) ? item.name_en : highlight(item.name_en!, searchValue)}`"
                  class="tw-text-base tw-font-normal" />
                <SvgoCommonIconCheck class="tw-w-5" v-if="isChecked(item.iso2_code) && multi" />
              </div>
            </div>
          </div>
          <div v-else class="tw-flex tw-flex-col tw-flex-1  tw-justify-center tw-items-center tw-py-10">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class=" tw-w-8 tw-h-8 tw-fill-neutral-300">
              <title>circle-off-outline</title>
              <path
                d="M22.11 21.46L2.39 1.73L1.11 3L4.06 5.95C2.78 7.63 2 9.72 2 12C2 17.5 6.5 22 12 22C14.28 22 16.37 21.23 18.05 19.94L20.84 22.73L22.11 21.46M12 20C7.58 20 4 16.42 4 12C4 10.27 4.56 8.68 5.5 7.38L16.62 18.5C15.32 19.45 13.73 20 12 20M8.17 4.97L6.72 3.5C8.25 2.56 10.06 2 12 2C17.5 2 22 6.5 22 12C22 13.94 21.44 15.75 20.5 17.28L19.03 15.83C19.65 14.69 20 13.39 20 12C20 7.58 16.42 4 12 4C10.61 4 9.31 4.35 8.17 4.97Z" />
            </svg>
          </div>
        </div>
      </template>
    </UIDropDown>
    <div class="tw-label tw-w-full tw-flex tw-items-center tw-py-2 tw-px-2" v-if="errorMessages?.length">
      <span class="tw-label-text-alt tw-text-error">{{ resolveErrorMessages(errorMessages).join('\n') }}</span>
    </div>
  </div>
</template>
<script setup lang="ts">
const visible = ref(false)

const emit = defineEmits<{
  (e: 'onItem', item?: CountryObject): void
  (e: 'change'): void
  (e: 'blur'): void
}>()
const props = defineProps<{ placeholder: string; errorMessages?: (string | Ref<string>)[]; multi?: boolean; disRiskyAreas?: boolean, disabled?: boolean, isGuest?: boolean }>()
const searchValue = ref<string>()
const model = defineModel<string | string[]>()

const open = () => {
  if (props.disabled) {
    return
  }
  visible.value = true
}


const getItem = (code: string) => {
  return unref(options).find((m) => m.iso2_code === code)
}
const getDefaultItem = () => {
  return unref(options).find((m) => m.default)
}

const selected = computed({
  get() {
    if (props.multi) {
      return ((model.value as string[]) || []).filter(Boolean)
    }
    return ([model.value] as string[]).filter(Boolean)
  },
  set(vals: string[]) {
    if (props.multi) {
      model.value = vals
    } else {
      model.value = vals.length ? vals[0] : ''
      // if (vals.length) {
      //   const item = getItem(vals[0] as string)
      //   emit('onItem', item)
      // }
    }
  },
})

const { common } = useApi()
const { data, isPending } = props.isGuest ? common.useGuestCountryListQuery() : common.useCountryQuery()
const options = computed(
  () =>
    unref(data)
      ?.data.filter((item) => item.iso2_code)
      .map((item) => {
        const index = ['US', 'SG', 'HK', 'JP', 'CN'].reverse().indexOf(item.iso2_code)
        return { ...item, index: index + 1 }
      })
      .sort((a, b) => b.index - a.index) ?? [],
)
const regions = computed(() => {
  return (
    unref(options).filter((item) =>
      searchValue.value ? item.name_en.toLocaleLowerCase().includes(searchValue.value.toLocaleLowerCase()) : true,
    ) ?? []
  )
})
const highlight = (name: string, searchName = '') => {
  return String(name).replace(new RegExp(searchName, 'ig'), (word) => `<span class='tw-text-primary tw-normal-case'>${word}</span>`)
}
const isRisky = (item: CountryObject) => {
  if (props.disRiskyAreas) {
    return false
  } else {
    return !item.status
  }
}
const onSelect = (item: CountryObject) => {
  if (isRisky(item)) {
    return false
  }
  const list = selected.value ?? []
  if (props.multi) {
    if (list.some((iso2_code) => iso2_code === item.iso2_code)) {
      selected.value = list.filter((iso2_code) => iso2_code !== item.iso2_code)
    } else {
      selected.value = [...list, item.iso2_code].filter(Boolean)
    }
  } else {
    selected.value = [item.iso2_code]
    visible.value = false
  }
  emit('change')
  // TODO
}

const isChecked = (iso2_code: string) => {
  const list = selected.value ?? []
  return list.some((m) => m === iso2_code)
}

const del = (iso2_code: string) => {
  const list = selected.value ?? []
  selected.value = list.filter((m) => m !== iso2_code)
  emit('blur')
}
// const { data: dataIp } = common.useIPQuery()
watchEffect(() => {
  if (!props.multi) {
    if (unref(selected).length) {
      const item = getItem(unref(selected)[0] as string)
      emit('onItem', item)
    } else {
      const item = getDefaultItem()
      if (item) {
        selected.value = [item?.iso2_code]
        emit('onItem', item)
      }
    }
  }
})
</script>
